import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import axios from 'axios';


const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';

const CancellationModal = ({ isOpen, onClose, orderId, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const cancellationReasons = [
    'Changed my mind',
    'Found a better price elsewhere',
    'Ordered by mistake',
    'Delivery time too long',
    'Need to modify order details',
    'Other'
  ];

  const handleSubmit = async () => {
    const finalReason = reason === 'Other' ? customReason : reason;
    
    if (!finalReason.trim()) {
      setError('Please select or enter a cancellation reason');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('userToken'); // Changed from 'token' to 'userToken'
      
      console.log('Token check:', token ? 'Token exists' : 'No token found');
      
      if (!token) {
        setError('You must be logged in to cancel an order');
        setSubmitting(false);
        return;
      }

      console.log('Sending cancellation request for order:', orderId);
      console.log('Reason:', finalReason);

      const response = await axios.post(
        `${API_BASE}/api/orders/${orderId}/cancel`,
        { reason: finalReason.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Cancellation successful:', response.data);
      onSuccess(response.data);
      onClose();
    } catch (err) {
      console.error('Cancellation error:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.response?.data?.message || 'Failed to cancel order. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">Request Order Cancellation</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={submitting}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Please select a reason for cancellation *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={submitting}
            >
              <option value="">Select a reason</option>
              {cancellationReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {reason === 'Other' && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Please specify
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter your reason..."
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                disabled={submitting}
              />
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Your cancellation request will be reviewed by our team. 
              If approved, any payment made will be refunded within 5-7 business days.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Go Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !reason || (reason === 'Other' && !customReason.trim())}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Request Cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancellationModal;
